import React, { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal, Nav, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchMeusPedidos,
    fetchTodosPedidos,
    criarPedidoMaterial,
    atualizarStatusPedido,
    excluirPedido,
    uploadNotaFiscal,
    downloadNotaFiscal,
    removerNotaFiscal
} from '../../services/api';
import Loading from '../../components/Loading/Loading';
import EmptyState from '../../components/EmptyState/EmptyState';
import styles from './PedidosMateriais.module.css';

const URGENCIA_OPTIONS = [
    { value: 'baixa', label: 'Baixa', color: 'secondary', icon: '🟢' },
    { value: 'media', label: 'Média', color: 'info', icon: '🔵' },
    { value: 'alta', label: 'Alta', color: 'warning', icon: '🟡' },
    { value: 'critica', label: 'Crítica', color: 'danger', icon: '🔴' }
];

const STATUS_OPTIONS = [
    { value: 'pendente', label: 'Pendente', color: 'warning', icon: '⏳' },
    { value: 'aprovado', label: 'Aprovado', color: 'success', icon: '✅' },
    { value: 'rejeitado', label: 'Rejeitado', color: 'danger', icon: '❌' },
    { value: 'em_andamento', label: 'Em Andamento', color: 'primary', icon: '🔄' },
    { value: 'concluido', label: 'Concluído', color: 'dark', icon: '✔️' }
];

const UNIDADE_OPTIONS = [
    { value: 'unidade', label: 'Unidade' },
    { value: 'pacote', label: 'Pacote' },
    { value: 'caixa', label: 'Caixa' },
    { value: 'kg', label: 'Kg' },
    { value: 'litro', label: 'Litro' },
    { value: 'metro', label: 'Metro' },
    { value: 'rolo', label: 'Rolo' },
    { value: 'par', label: 'Par' }
];

const PedidosMateriais = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAdmin, user } = useAuth();

    // Estados
    const [activeTab, setActiveTab] = useState('meus');
    const [showModal, setShowModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedPedido, setSelectedPedido] = useState(null);
    const [filtroStatus, setFiltroStatus] = useState('');
    const [filtroUrgencia, setFiltroUrgencia] = useState('');
    const [uploadingId, setUploadingId] = useState(null);
    const fileInputRef = useRef(null);
    const [alert, setAlert] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        material: '',
        quantidade: 1,
        unidade: 'unidade',
        urgencia: 'media',
        justificativa: ''
    });

    // Status update state
    const [statusUpdate, setStatusUpdate] = useState({
        status: '',
        observacao_admin: ''
    });

    // Queries
    const { data: meusPedidosData, isLoading: loadingMeus, refetch: refetchMeus } = useQuery({
        queryKey: ['meus-pedidos', { status: filtroStatus, urgencia: filtroUrgencia }],
        queryFn: () => fetchMeusPedidos({ status: filtroStatus, urgencia: filtroUrgencia }),
        enabled: activeTab === 'meus'
    });

    const { data: todosPedidosData, isLoading: loadingTodos, refetch: refetchTodos } = useQuery({
        queryKey: ['todos-pedidos', { status: filtroStatus, urgencia: filtroUrgencia }],
        queryFn: () => fetchTodosPedidos({ status: filtroStatus, urgencia: filtroUrgencia }),
        enabled: activeTab === 'todos' && isAdmin()
    });

    // Mutations
    const criarMutation = useMutation({
        mutationFn: criarPedidoMaterial,
        onSuccess: () => {
            queryClient.invalidateQueries(['meus-pedidos']);
            queryClient.invalidateQueries(['todos-pedidos']);
            setShowModal(false);
            setFormData({
                material: '',
                quantidade: 1,
                unidade: 'unidade',
                urgencia: 'media',
                justificativa: ''
            });
            setAlert({ type: 'success', message: 'Pedido de material criado com sucesso!' });
            setTimeout(() => setAlert(null), 3000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao criar pedido' });
        }
    });

    const statusMutation = useMutation({
        mutationFn: ({ id, data }) => atualizarStatusPedido(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['meus-pedidos']);
            queryClient.invalidateQueries(['todos-pedidos']);
            setShowStatusModal(false);
            setSelectedPedido(null);
            setStatusUpdate({ status: '', observacao_admin: '' });
            setAlert({ type: 'success', message: 'Status atualizado com sucesso!' });
            setTimeout(() => setAlert(null), 3000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao atualizar status' });
        }
    });

    const excluirMutation = useMutation({
        mutationFn: excluirPedido,
        onSuccess: () => {
            queryClient.invalidateQueries(['meus-pedidos']);
            queryClient.invalidateQueries(['todos-pedidos']);
            setAlert({ type: 'success', message: 'Pedido excluído com sucesso!' });
            setTimeout(() => setAlert(null), 3000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao excluir pedido' });
        }
    });

    // Mutation para upload de nota fiscal
    const uploadMutation = useMutation({
        mutationFn: ({ id, file }) => uploadNotaFiscal(id, file),
        onSuccess: () => {
            queryClient.invalidateQueries(['meus-pedidos']);
            queryClient.invalidateQueries(['todos-pedidos']);
            setShowUploadModal(false);
            setSelectedPedido(null);
            setUploadingId(null);
            setAlert({ type: 'success', message: 'Nota fiscal enviada com sucesso!' });
            setTimeout(() => setAlert(null), 3000);
        },
        onError: (error) => {
            setUploadingId(null);
            setAlert({ type: 'danger', message: error.message || 'Erro ao enviar nota fiscal' });
        }
    });

    // Mutation para remover nota fiscal
    const removerNotaMutation = useMutation({
        mutationFn: removerNotaFiscal,
        onSuccess: () => {
            queryClient.invalidateQueries(['meus-pedidos']);
            queryClient.invalidateQueries(['todos-pedidos']);
            setAlert({ type: 'success', message: 'Nota fiscal removida com sucesso!' });
            setTimeout(() => setAlert(null), 3000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao remover nota fiscal' });
        }
    });

    // Handlers
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.material.trim() || formData.quantidade < 1) {
            setAlert({ type: 'warning', message: 'Preencha o material e a quantidade corretamente' });
            return;
        }
        criarMutation.mutate(formData);
    };

    const handleStatusUpdate = (e) => {
        e.preventDefault();
        if (!statusUpdate.status) {
            setAlert({ type: 'warning', message: 'Selecione um status' });
            return;
        }
        statusMutation.mutate({ id: selectedPedido.id, data: statusUpdate });
    };

    const handleExcluir = (pedido) => {
        if (window.confirm(`Tem certeza que deseja excluir o pedido "${pedido.material}"?`)) {
            excluirMutation.mutate(pedido.id);
        }
    };

    const abrirModalStatus = (pedido) => {
        setSelectedPedido(pedido);
        setStatusUpdate({ status: pedido.status, observacao_admin: pedido.observacao_admin || '' });
        setShowStatusModal(true);
    };

    // Handler para abrir modal de upload
    const abrirModalUpload = (pedido) => {
        setSelectedPedido(pedido);
        setShowUploadModal(true);
    };

    // Handler para upload de arquivo
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file && selectedPedido) {
            setUploadingId(selectedPedido.id);
            uploadMutation.mutate({ id: selectedPedido.id, file });
        }
    };

    // Handler para download de nota fiscal
    const handleDownloadNota = async (pedido) => {
        try {
            const blob = await downloadNotaFiscal(pedido.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = pedido.nota_fiscal_nome || 'nota_fiscal';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setAlert({ type: 'danger', message: 'Erro ao baixar nota fiscal' });
        }
    };

    // Handler para remover nota fiscal
    const handleRemoverNota = (pedido) => {
        if (window.confirm('Tem certeza que deseja remover a nota fiscal deste pedido?')) {
            removerNotaMutation.mutate(pedido.id);
        }
    };

    const getUrgenciaBadge = (urgencia) => {
        const opt = URGENCIA_OPTIONS.find(o => o.value === urgencia);
        return opt ? (
            <Badge bg={opt.color} className="d-flex align-items-center gap-1">
                <span>{opt.icon}</span> {opt.label}
            </Badge>
        ) : <Badge bg="secondary">{urgencia}</Badge>;
    };

    const getStatusBadge = (status) => {
        const opt = STATUS_OPTIONS.find(o => o.value === status);
        return opt ? (
            <Badge bg={opt.color} className="d-flex align-items-center gap-1">
                <span>{opt.icon}</span> {opt.label}
            </Badge>
        ) : <Badge bg="secondary">{status}</Badge>;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const pedidos = activeTab === 'meus' ? (meusPedidosData?.data || []) : (todosPedidosData?.data || []);
    const stats = todosPedidosData?.stats || {};
    const isLoading = activeTab === 'meus' ? loadingMeus : loadingTodos;

    return (
        <Container fluid className="p-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center">
                    <Button
                        variant="link"
                        className="p-0 me-3 text-decoration-none text-secondary"
                        onClick={() => navigate(-1)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </Button>
                    <div>
                        <h1 className="h3 mb-0 text-gray-800">📦 Pedidos de Materiais</h1>
                        <p className="mb-0 text-muted">Solicite materiais necessários para o seu trabalho</p>
                    </div>
                </div>
                <Button variant="primary" onClick={() => setShowModal(true)} className={styles.btnNovoPedido}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Novo Pedido
                </Button>
            </div>

            {/* Alert */}
            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mb-4">
                    {alert.message}
                </Alert>
            )}

            {/* Stats Cards - Admin apenas */}
            {isAdmin() && activeTab === 'todos' && stats && (
                <Row className="mb-4 g-3">
                    <Col xl={2} md={4} sm={6}>
                        <div className={styles.periodCard}>
                            <div className={`${styles.periodCardBorder} ${styles.borderWarning}`}></div>
                            <div className={styles.periodCardContent}>
                                <div className={styles.periodCardInfo}>
                                    <span className={styles.periodCardLabel}>PENDENTES</span>
                                    <span className={styles.periodCardValue}>{stats.pendentes || 0}</span>
                                </div>
                                <div className={`${styles.periodCardIcon} ${styles.iconWarning}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xl={2} md={4} sm={6}>
                        <div className={styles.periodCard}>
                            <div className={`${styles.periodCardBorder} ${styles.borderSuccess}`}></div>
                            <div className={styles.periodCardContent}>
                                <div className={styles.periodCardInfo}>
                                    <span className={styles.periodCardLabel}>APROVADOS</span>
                                    <span className={styles.periodCardValue}>{stats.aprovados || 0}</span>
                                </div>
                                <div className={`${styles.periodCardIcon} ${styles.iconSuccess}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xl={2} md={4} sm={6}>
                        <div className={styles.periodCard}>
                            <div className={`${styles.periodCardBorder} ${styles.borderPrimary}`}></div>
                            <div className={styles.periodCardContent}>
                                <div className={styles.periodCardInfo}>
                                    <span className={styles.periodCardLabel}>EM ANDAMENTO</span>
                                    <span className={styles.periodCardValue}>{stats.em_andamento || 0}</span>
                                </div>
                                <div className={`${styles.periodCardIcon} ${styles.iconPrimary}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 12a9 9 0 11-6.219-8.56"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xl={2} md={4} sm={6}>
                        <div className={styles.periodCard}>
                            <div className={`${styles.periodCardBorder} ${styles.borderDark}`}></div>
                            <div className={styles.periodCardContent}>
                                <div className={styles.periodCardInfo}>
                                    <span className={styles.periodCardLabel}>CONCLUÍDOS</span>
                                    <span className={styles.periodCardValue}>{stats.concluidos || 0}</span>
                                </div>
                                <div className={`${styles.periodCardIcon} ${styles.iconDark}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xl={2} md={4} sm={6}>
                        <div className={styles.periodCard}>
                            <div className={`${styles.periodCardBorder} ${styles.borderDanger}`}></div>
                            <div className={styles.periodCardContent}>
                                <div className={styles.periodCardInfo}>
                                    <span className={styles.periodCardLabel}>REJEITADOS</span>
                                    <span className={styles.periodCardValue}>{stats.rejeitados || 0}</span>
                                </div>
                                <div className={`${styles.periodCardIcon} ${styles.iconDanger}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="15" y1="9" x2="9" y2="15"></line>
                                        <line x1="9" y1="9" x2="15" y2="15"></line>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xl={2} md={4} sm={6}>
                        <div className={styles.periodCard}>
                            <div className={`${styles.periodCardBorder} ${styles.borderCritical}`}></div>
                            <div className={styles.periodCardContent}>
                                <div className={styles.periodCardInfo}>
                                    <span className={styles.periodCardLabel}>CRÍTICOS</span>
                                    <span className={styles.periodCardValue}>{stats.criticos_pendentes || 0}</span>
                                </div>
                                <div className={`${styles.periodCardIcon} ${styles.iconCritical}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            {/* Main Card */}
            <Card className="shadow mb-4">
                <Card.Header className="py-3 bg-white">
                    <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="card-header-tabs">
                        <Nav.Item>
                            <Nav.Link eventKey="meus" className="text-dark fw-bold">
                                <span className="me-2">📋</span> Meus Pedidos
                            </Nav.Link>
                        </Nav.Item>
                        {isAdmin() && (
                            <Nav.Item>
                                <Nav.Link eventKey="todos" className="text-dark fw-bold">
                                    <span className="me-2">📊</span> Todos os Pedidos
                                </Nav.Link>
                            </Nav.Item>
                        )}
                    </Nav>
                </Card.Header>
                <Card.Body>
                    {/* Filtros */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small text-muted">Filtrar por Status</Form.Label>
                                <Form.Select
                                    value={filtroStatus}
                                    onChange={(e) => setFiltroStatus(e.target.value)}
                                    className={styles.filterSelect}
                                >
                                    <option value="">Todos os status</option>
                                    {STATUS_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small text-muted">Filtrar por Urgência</Form.Label>
                                <Form.Select
                                    value={filtroUrgencia}
                                    onChange={(e) => setFiltroUrgencia(e.target.value)}
                                    className={styles.filterSelect}
                                >
                                    <option value="">Todas as urgências</option>
                                    {URGENCIA_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                            {(filtroStatus || filtroUrgencia) && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => { setFiltroStatus(''); setFiltroUrgencia(''); }}
                                    className="w-100"
                                >
                                    ✕ Limpar Filtros
                                </Button>
                            )}
                        </Col>
                    </Row>

                    {/* Loading */}
                    {isLoading && <Loading />}

                    {/* Empty State */}
                    {!isLoading && pedidos.length === 0 && (
                        <EmptyState
                            message={activeTab === 'meus'
                                ? "Você ainda não fez nenhum pedido de material. Clique em 'Novo Pedido' para começar!"
                                : "Nenhum pedido de material encontrado com os filtros selecionados."
                            }
                        />
                    )}

                    {/* Tabela de Pedidos */}
                    {!isLoading && pedidos.length > 0 && (
                        <div className="table-responsive">
                            <Table hover className={styles.pedidosTable}>
                                <thead className="bg-light">
                                    <tr>
                                        <th>Material</th>
                                        <th className="text-center">Qtd</th>
                                        <th className="text-center">Urgência</th>
                                        <th className="text-center">Status</th>
                                        {activeTab === 'todos' && <th>Solicitante</th>}
                                        <th>Data</th>
                                        <th>Justificativa</th>
                                        <th className="text-center">Nota Fiscal</th>
                                        <th className="text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pedidos.map((pedido) => (
                                        <tr key={pedido.id} className={styles.pedidoRow}>
                                            <td className="fw-bold">{pedido.material}</td>
                                            <td className="text-center">
                                                <span className="badge bg-light text-dark">
                                                    {pedido.quantidade} {pedido.unidade}
                                                </span>
                                            </td>
                                            <td className="text-center">{getUrgenciaBadge(pedido.urgencia)}</td>
                                            <td className="text-center">{getStatusBadge(pedido.status)}</td>
                                            {activeTab === 'todos' && (
                                                <td>
                                                    <small className="text-muted">{pedido.usuario_nome}</small>
                                                </td>
                                            )}
                                            <td>
                                                <small className="text-muted">{formatDate(pedido.data_criacao)}</small>
                                            </td>
                                            <td>
                                                <div className={styles.justificativaCell}>
                                                    {pedido.justificativa || <span className="text-muted">-</span>}
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                {pedido.nota_fiscal_path ? (
                                                    <div className="d-flex gap-1 justify-content-center">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>Baixar: {pedido.nota_fiscal_nome}</Tooltip>}
                                                        >
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => handleDownloadNota(pedido)}
                                                                className={styles.btnNotaFiscal}
                                                            >
                                                                📄
                                                            </Button>
                                                        </OverlayTrigger>
                                                        {(isAdmin() || pedido.usuario_id === user?.id) && (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Remover nota fiscal</Tooltip>}
                                                            >
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoverNota(pedido)}
                                                                    className={styles.btnNotaFiscal}
                                                                >
                                                                    ✕
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>Anexar nota fiscal</Tooltip>}
                                                    >
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => abrirModalUpload(pedido)}
                                                            disabled={uploadingId === pedido.id}
                                                            className={styles.btnNotaFiscal}
                                                        >
                                                            {uploadingId === pedido.id ? '⏳' : '📎'}
                                                        </Button>
                                                    </OverlayTrigger>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex gap-2 justify-content-center">
                                                    {isAdmin() && (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => abrirModalStatus(pedido)}
                                                            title="Alterar Status"
                                                        >
                                                            📝
                                                        </Button>
                                                    )}
                                                    {(pedido.status === 'pendente' || isAdmin()) && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleExcluir(pedido)}
                                                            title="Excluir"
                                                        >
                                                            🗑️
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Modal Novo Pedido */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton className={styles.modalHeader}>
                    <Modal.Title>📦 Novo Pedido de Material</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Material *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Digite o nome do material"
                                value={formData.material}
                                onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                                required
                                className={styles.formInput}
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Quantidade *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={formData.quantidade}
                                        onChange={(e) => setFormData({ ...formData, quantidade: parseInt(e.target.value) || 1 })}
                                        required
                                        className={styles.formInput}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Unidade</Form.Label>
                                    <Form.Select
                                        value={formData.unidade}
                                        onChange={(e) => setFormData({ ...formData, unidade: e.target.value })}
                                        className={styles.formInput}
                                    >
                                        {UNIDADE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Urgência</Form.Label>
                            <div className={styles.urgenciaOptions}>
                                {URGENCIA_OPTIONS.map(opt => (
                                    <Button
                                        key={opt.value}
                                        type="button"
                                        variant={formData.urgencia === opt.value ? opt.color : `outline-${opt.color}`}
                                        onClick={() => setFormData({ ...formData, urgencia: opt.value })}
                                        className={styles.urgenciaBtn}
                                    >
                                        {opt.icon} {opt.label}
                                    </Button>
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Justificativa</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Explique por que você precisa deste material..."
                                value={formData.justificativa}
                                onChange={(e) => setFormData({ ...formData, justificativa: e.target.value })}
                                className={styles.formInput}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={criarMutation.isPending}
                            className={styles.btnSubmit}
                        >
                            {criarMutation.isPending ? 'Enviando...' : '✅ Enviar Pedido'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal Atualizar Status (Admin) */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton className={styles.modalHeader}>
                    <Modal.Title>📝 Atualizar Status</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStatusUpdate}>
                    <Modal.Body>
                        {selectedPedido && (
                            <div className="mb-3 p-3 bg-light rounded">
                                <strong>Material:</strong> {selectedPedido.material}<br />
                                <strong>Quantidade:</strong> {selectedPedido.quantidade} {selectedPedido.unidade}<br />
                                <strong>Solicitante:</strong> {selectedPedido.usuario_nome}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Novo Status *</Form.Label>
                            <Form.Select
                                value={statusUpdate.status}
                                onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                                required
                                className={styles.formInput}
                            >
                                <option value="">Selecione um status</option>
                                {STATUS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Observação do Administrador</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Adicione uma observação (opcional)..."
                                value={statusUpdate.observacao_admin}
                                onChange={(e) => setStatusUpdate({ ...statusUpdate, observacao_admin: e.target.value })}
                                className={styles.formInput}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={statusMutation.isPending}
                            className={styles.btnSubmit}
                        >
                            {statusMutation.isPending ? 'Salvando...' : '💾 Salvar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal Upload de Nota Fiscal */}
            <Modal show={showUploadModal} onHide={() => { setShowUploadModal(false); setSelectedPedido(null); }} centered>
                <Modal.Header closeButton className={styles.modalHeader}>
                    <Modal.Title>📎 Anexar Nota Fiscal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedPedido && (
                        <div className="mb-3 p-3 bg-light rounded">
                            <strong>Material:</strong> {selectedPedido.material}<br />
                            <strong>Quantidade:</strong> {selectedPedido.quantidade} {selectedPedido.unidade}<br />
                            <strong>Status:</strong> {getStatusBadge(selectedPedido.status)}
                        </div>
                    )}

                    <div className={styles.uploadArea}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.xml"
                            className={styles.fileInput}
                            id="notaFiscalInput"
                        />
                        <label htmlFor="notaFiscalInput" className={styles.uploadLabel}>
                            <div className={styles.uploadIcon}>📁</div>
                            <span className={styles.uploadText}>
                                Clique para selecionar um arquivo
                            </span>
                            <span className={styles.uploadHint}>
                                Formatos aceitos: PDF, Imagens (JPG, PNG, WebP), XML
                            </span>
                            <span className={styles.uploadHint}>
                                Tamanho máximo: 10MB
                            </span>
                        </label>
                    </div>

                    {uploadMutation.isPending && (
                        <div className="text-center mt-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Enviando...</span>
                            </div>
                            <p className="mt-2 text-muted">Enviando nota fiscal...</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowUploadModal(false); setSelectedPedido(null); }}>
                        Cancelar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default PedidosMateriais;
